/**
 * DA Custom Product Attributes
 *
 * Shared CPA engine for:
 * - Standard product customization
 * - Bulk-order customization
 * - Future review/edit build interfaces
 *
 * Handles:
 * - Independent product and bulk CPA instances
 * - Group tab switching
 * - Shared group setup fields
 * - Application-specific fields
 * - Cross-scope conditional fields
 * - Application toggles
 * - Button/swatch selected states
 * - Required field validation/highlighting
 * - Group-level optional notes
 * - Filtered Decoration Method options
 * - Automatic single Decoration Method selection
 * - Option info modal
 * - Option image preview modal
 * - Placement guide modal trigger
 * - Featured examples toggle and modal trigger
 * - Selection-only live summary
 * - Product and bulk-aware pricing summaries
 * - Summary application removal/reset with fade transitions
 * - Main product + add-on AJAX cart payload
 * - Reusable bulk cart-item generation API
 *
 * Setup fields:
 * - Render once per customization group
 * - Use data-cpa-field-scope="setup"
 * - May control conditions inside application panels
 * - Are validated once per group
 * - Are copied into customized parent and add-on properties
 *
 * File inputs:
 * - The selected filename is used for validation, summaries, and properties
 * - The actual file is not uploaded by this script
 * - A hosted upload solution will be required separately
 *
 * Group notes:
 * - Display normally in the summary
 * - Do not count as customization data by themselves
 * - Are stored only on the parent product
 * - Use private properties in this format:
 *   _CPA Note: Lettering Notes
 *
 * Decoration Method:
 * - Field key: decoration_method
 * - One enabled method is automatically selected and visually hidden
 * - Automatic selections do not activate a group by themselves
 * - Automatic selections are restored when an application is reset
 * - Multiple enabled methods remain customer-selectable
 *
 * Supported add-on modes:
 * - once_per_group
 * - once_per_application
 * - quantity_from_field
 * - variant_from_application
 * - variant_from_option
 */

(function () {
  window.DACPA = window.DACPA || {};

  window.DACPA.instances =
    window.DACPA.instances instanceof Map
      ? window.DACPA.instances
      : new Map();

  window.DACPA.getInstance = function (
    identifier
  ) {
    return (
      window.DACPA.instances.get(
        identifier
      ) || null
    );
  };

  window.DACPA.getInstanceByContext =
    function (
      context,
      instanceId
    ) {
      return (
        window.DACPA.instances.get(
          `${context}:${instanceId}`
        ) || null
      );
    };

  function escapeSelector(value) {
    if (
      window.CSS &&
      typeof window.CSS.escape ===
        'function'
    ) {
      return window.CSS.escape(
        String(value)
      );
    }

    return String(value).replace(
      /["\\]/g,
      '\\$&'
    );
  }

  function escapeHtml(value) {
    return String(
      value == null ? '' : value
    )
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function normalizeBoolean(value) {
    return (
      value === true ||
      value === 'true' ||
      value === 'True' ||
      value === '1'
    );
  }

  function normalizeValue(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/-/g, '_');
  }

  function parseCents(value) {
    const cents = parseInt(
      value,
      10
    );

    return Number.isFinite(cents)
      ? cents
      : 0;
  }

  function formatMoney(cents) {
    const safeCents =
      Number.isFinite(cents)
        ? cents
        : 0;

    return new Intl.NumberFormat(
      'en-US',
      {
        style: 'currency',
        currency: 'USD'
      }
    ).format(safeCents / 100);
  }

  function makeCustomizationId() {
    return `cpa-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 10)}`;
  }

  function makeBulkBuildId() {
    return `bulk-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 10)}`;
  }

  function findProductForm(root) {
    return (
      root.closest(
        'form[action*="/cart/add"]'
      ) ||
      document.querySelector(
        '[data-cpa-product-form]'
      ) ||
      document.querySelector(
        'form[action*="/cart/add"]'
      )
    );
  }

  function findAddButton(form) {
    if (!form) return null;

    return (
      form.querySelector(
        '[type="submit"][name="add"]'
      ) ||
      form.querySelector(
        'button[name="add"]'
      ) ||
      form.querySelector(
        '[type="submit"]'
      ) ||
      form.querySelector(
        '.ProductForm__AddToCart'
      )
    );
  }

  function findMainVariantId(form) {
    if (!form) return '';

    const idInput =
      form.querySelector(
        '[name="id"]'
      );

    return idInput
      ? idInput.value
      : '';
  }

  function findMainQuantity(form) {
    if (!form) return 1;

    const quantityInput =
      form.querySelector(
        '[name="quantity"]'
      ) ||
      form.querySelector(
        'input.QuantitySelector__CurrentQuantity'
      ) ||
      form.querySelector(
        'input[data-quantity-input]'
      );

    const quantity =
      quantityInput
        ? parseInt(
            quantityInput.value,
            10
          )
        : 1;

    return (
      Number.isFinite(quantity) &&
      quantity > 0
        ? quantity
        : 1
    );
  }

  function initCustomProductAttributes(
    root
  ) {
    if (
      !root ||
      root.dataset.cpaInitialized ===
        'true'
    ) {
      return;
    }

    root.dataset.cpaInitialized =
      'true';

    const context =
      root.dataset.cpaContext ||
      'product';

    const instanceId =
      root.dataset.cpaInstanceId ||
      root.id;

    const registryKey =
      `${context}:${instanceId}`;

    const groups = Array.from(
      root.querySelectorAll(
        '[data-cpa-group]'
      )
    );

    const summaryContent =
      root.querySelector(
        '[data-cpa-summary-content]'
      );

      const accordionToggle =
  root.querySelector(
    '[data-cpa-accordion-toggle]'
  );

const accordionPanel =
  root.querySelector(
    '[data-cpa-accordion-panel]'
  );

    const productForm =
      context === 'product'
        ? findProductForm(root)
        : null;

    let bulkSelectionItems = [];

    let previewModal =
      root.querySelector(
        '[data-cpa-preview-modal]'
      );

    let infoModal =
      root.querySelector(
        '[data-cpa-info-modal]'
      );

    function waitForSummaryAnimation(
      callback,
      delay
    ) {
      window.setTimeout(
        callback,
        delay || 240
      );
    }

    function isCpaPanelMostlyVisible() {
      const rect =
        root.getBoundingClientRect();

      const viewportHeight =
        window.innerHeight ||
        document.documentElement
          .clientHeight;

      return (
        rect.top >= 0 &&
        rect.top <
          viewportHeight * 0.45
      );
    }

    function scrollToCpaPanelIfNeeded() {
      if (
        isCpaPanelMostlyVisible()
      ) {
        return;
      }

      const offset = 90;

      const top =
        root
          .getBoundingClientRect()
          .top +
        window.pageYOffset -
        offset;

      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    }

    function scrollToField(field) {
      if (!field) return;

      const offset = 120;

      const top =
        field
          .getBoundingClientRect()
          .top +
        window.pageYOffset -
        offset;

      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    }

    function setCpaAccordionOpen(
  isOpen
) {
  if (
    !accordionToggle ||
    !accordionPanel
  ) {
    return;
  }

  accordionToggle.setAttribute(
    'aria-expanded',
    isOpen ? 'true' : 'false'
  );

  accordionPanel.hidden =
    !isOpen;
}

    function getProductVariantPriceMap() {
      const cpaJson =
        root.querySelector(
          '[data-cpa-product-variants-json]'
        );

      const fallbackJson =
        document.querySelector(
          '[id^="DA-ProductVariantsJson-"]'
        );

      if (
        cpaJson &&
        cpaJson.textContent.trim()
      ) {
        try {
          return JSON.parse(
            cpaJson.textContent
          );
        } catch (error) {
          console.warn(
            'Unable to parse CPA product variant price JSON.',
            error
          );
        }
      }

      if (
        fallbackJson &&
        fallbackJson.textContent.trim()
      ) {
        try {
          const variants =
            JSON.parse(
              fallbackJson.textContent
            );

          return variants.reduce(
            function (
              map,
              variant
            ) {
              map[
                String(variant.id)
              ] = {
                price: parseCents(
                  variant.price
                ),
                compare_at_price:
                  parseCents(
                    variant
                      .compare_at_price
                  )
              };

              return map;
            },
            {}
          );
        } catch (error) {
          console.warn(
            'Unable to parse fallback product variant JSON.',
            error
          );
        }
      }

      return {};
    }

    function getVariantPriceCents(
      variantId
    ) {
      const priceMap =
        getProductVariantPriceMap();

      const variantPrice =
        priceMap[
          String(variantId)
        ];

      return parseCents(
        variantPrice &&
          variantPrice.price
      );
    }

    function getMainProductPriceCents() {
      return getVariantPriceCents(
        findMainVariantId(
          productForm
        )
      );
    }

    function normalizeBulkSelection(
      items
    ) {
      if (!Array.isArray(items)) {
        return [];
      }

      return items
        .map(function (item) {
          return {
            id: Number(item.id),
            quantity: Number(
              item.quantity
            )
          };
        })
        .filter(function (item) {
          return (
            Number.isFinite(
              item.id
            ) &&
            item.id > 0 &&
            Number.isFinite(
              item.quantity
            ) &&
            item.quantity > 0
          );
        });
    }

    function getBulkSelectionQuantity() {
      return bulkSelectionItems.reduce(
        function (total, item) {
          return (
            total +
            item.quantity
          );
        },
        0
      );
    }

    function getBulkSelectionProductTotal() {
      return bulkSelectionItems.reduce(
        function (total, item) {
          return (
            total +
            getVariantPriceCents(
              item.id
            ) *
              item.quantity
          );
        },
        0
      );
    }

    function setBulkSelection(items) {
      bulkSelectionItems =
        normalizeBulkSelection(
          items
        );

      updateSummary();

      root.dispatchEvent(
        new CustomEvent(
          'cpa:bulk-selection-updated',
          {
            bubbles: true,
            detail: {
              items:
                bulkSelectionItems.slice(),
              totalQuantity:
                getBulkSelectionQuantity()
            }
          }
        )
      );
    }

    function getGroupToggle(group) {
      if (!group) return null;

      return group.querySelector(
        '[data-cpa-group-toggle]'
      );
    }

    function getGroupPanel(group) {
      if (!group) return null;

      return group.querySelector(
        '[data-cpa-group-panel]'
      );
    }

    function getSetupPanel(group) {
      if (!group) return null;

      return group.querySelector(
        '[data-cpa-setup-panel]'
      );
    }

    function getActiveGroup() {
      return groups.find(
        function (group) {
          const panel =
            getGroupPanel(group);

          return (
            panel &&
            !panel.hidden
          );
        }
      );
    }

    function activateGroup(
      groupToActivate
    ) {
      if (!groupToActivate) {
        return;
      }

      groups.forEach(
        function (group) {
          const toggle =
            getGroupToggle(group);

          const panel =
            getGroupPanel(group);

          const isActive =
            group ===
            groupToActivate;

          if (toggle) {
            toggle.setAttribute(
              'aria-expanded',
              isActive
                ? 'true'
                : 'false'
            );

            toggle.classList.toggle(
              'is-selected',
              isActive
            );
          }

          if (panel) {
            panel.hidden =
              !isActive;
          }
        }
      );

      updateAllConditions();
      updateSummary();
      updateOptionMetaActions();
    }

    function initGroupTabs() {
      if (!groups.length) {
        return;
      }

      let activeGroup =
        getActiveGroup();

      if (!activeGroup) {
        activeGroup = groups[0];
      }

      activateGroup(activeGroup);
    }

    function getActiveApplicationPanels(
      group
    ) {
      if (!group) return [];

      return Array.from(
        group.querySelectorAll(
          '[data-cpa-application-panel]'
        )
      ).filter(function (panel) {
        return !panel.hidden;
      });
    }

    function getFieldScope(field) {
      if (!field) {
        return 'application';
      }

      return (
        field.dataset.cpaFieldScope ||
        field.dataset.fieldScope ||
        'application'
      );
    }

    function getFieldPanel(field) {
      if (!field) return null;

      if (
        getFieldScope(field) ===
        'setup'
      ) {
        return field.closest(
          '[data-cpa-setup-panel]'
        );
      }

      return field.closest(
        '[data-cpa-application-panel]'
      );
    }

    function getField(
      panel,
      fieldKey
    ) {
      if (
        !panel ||
        !fieldKey
      ) {
        return null;
      }

      return panel.querySelector(
        `[data-cpa-field][data-field-key="${escapeSelector(
          fieldKey
        )}"]`
      );
    }

    function getSetupField(
      group,
      fieldKey
    ) {
      const setupPanel =
        getSetupPanel(group);

      return getField(
        setupPanel,
        fieldKey
      );
    }

    function getApplicationField(
      panel,
      fieldKey
    ) {
      return getField(
        panel,
        fieldKey
      );
    }

    function resolveField(
      panel,
      fieldKey
    ) {
      if (
        !panel ||
        !fieldKey
      ) {
        return null;
      }

      const directField =
        getField(
          panel,
          fieldKey
        );

      if (directField) {
        return directField;
      }

      const group =
        panel.closest(
          '[data-cpa-group]'
        );

      if (!group) {
        return null;
      }

      return getSetupField(
        group,
        fieldKey
      );
    }

    function resolveConditionSourceField(
      field,
      panel,
      sourceFieldKey
    ) {
      if (
        !field ||
        !panel ||
        !sourceFieldKey
      ) {
        return null;
      }

      const fieldScope =
        getFieldScope(field);

      if (
        fieldScope === 'setup'
      ) {
        return getField(
          panel,
          sourceFieldKey
        );
      }

      const applicationField =
        getApplicationField(
          panel,
          sourceFieldKey
        );

      if (applicationField) {
        return applicationField;
      }

      const group =
        field.closest(
          '[data-cpa-group]'
        );

      return getSetupField(
        group,
        sourceFieldKey
      );
    }

    function getFieldInput(field) {
      if (!field) return null;

      return field.querySelector(
        '[data-cpa-input]'
      );
    }

    function isFileInput(input) {
      return Boolean(
        input &&
          input.matches(
            'input[type="file"]'
          )
      );
    }

    function getFileName(input) {
      if (
        !isFileInput(input) ||
        !input.files ||
        !input.files[0]
      ) {
        return '';
      }

      return input.files[0].name;
    }

        function isAutoSelectedField(field) {
      return Boolean(
        field &&
          normalizeBoolean(
            field.dataset
              .cpaAutoSelected
          )
      );
    }

    function getAutoSelectedValue(
      field
    ) {
      if (!field) return '';

      return String(
        field.dataset.cpaAutoValue ||
          ''
      ).trim();
    }

    function getAutoSelectedLabel(
      field
    ) {
      if (!field) return '';

      return String(
        field.dataset.cpaAutoLabel ||
          ''
      ).trim();
    }

    function restoreAutoSelectedField(
      field
    ) {
      if (
        !isAutoSelectedField(field)
      ) {
        return false;
      }

      const autoValue =
        getAutoSelectedValue(field);

      if (!autoValue) {
        return false;
      }

      const select =
        field.querySelector(
          'select[data-cpa-input]'
        );

      if (select) {
        select.value = autoValue;

        if (
          select.value !==
          autoValue
        ) {
          const matchingOption =
            Array.from(
              select.options
            ).find(
              function (option) {
                return (
                  normalizeValue(
                    option.value
                  ) ===
                  normalizeValue(
                    autoValue
                  )
                );
              }
            );

          if (matchingOption) {
            select.value =
              matchingOption.value;
          }
        }
      }

      const hiddenChoiceInput =
        field.querySelector(
          '[data-cpa-choice-value]'
        );

      if (hiddenChoiceInput) {
        hiddenChoiceInput.value =
          autoValue;
      }

      const choices =
        Array.from(
          field.querySelectorAll(
            '[data-cpa-choice]'
          )
        );

      let selectedChoice = null;

      choices.forEach(
        function (choice) {
          const isSelected =
            normalizeValue(
              choice.dataset.value
            ) ===
            normalizeValue(
              autoValue
            );

          choice.setAttribute(
            'aria-pressed',
            isSelected
              ? 'true'
              : 'false'
          );

          choice.classList.toggle(
            'is-selected',
            isSelected
          );

          if (isSelected) {
            selectedChoice =
              choice;
          }
        }
      );

      const standardInput =
        field.querySelector(
          '[data-cpa-input]:not([data-cpa-choice-value]):not(select)'
        );

      if (
        standardInput &&
        standardInput.matches(
          'input:not([type="file"]), textarea'
        )
      ) {
        standardInput.value =
          autoValue;
      }

      const swatchLabel =
        field.querySelector(
          '[data-cpa-swatch-label]'
        );

      if (swatchLabel) {
        swatchLabel.textContent =
          (selectedChoice &&
            (selectedChoice.dataset
              .label ||
              selectedChoice
                .textContent
                .trim())) ||
          getAutoSelectedLabel(
            field
          ) ||
          autoValue;
      }

      clearFieldInvalid(field);

      return true;
    }

    function initializeAutoSelectedFields() {
      root
        .querySelectorAll(
          '[data-cpa-field][data-cpa-auto-selected="true"]'
        )
        .forEach(
          function (field) {
            restoreAutoSelectedField(
              field
            );
          }
        );
    }

    function getFieldRawValueFromField(
      field
    ) {
      if (
        !field ||
        field.hidden
      ) {
        return '';
      }

      const input =
        getFieldInput(field);

      if (
        !input ||
        input.disabled
      ) {
        return '';
      }

      if (isFileInput(input)) {
        return getFileName(input);
      }

      if (
        input.matches('select')
      ) {
        return input.value || '';
      }

      if (
        input.matches(
          'input, textarea'
        )
      ) {
        return input.value || '';
      }

      return '';
    }

    function getFieldDisplayValueFromField(
      field
    ) {
      if (
        !field ||
        field.hidden
      ) {
        return '';
      }

      const input =
        getFieldInput(field);

      if (
        !input ||
        input.disabled
      ) {
        return '';
      }

      if (isFileInput(input)) {
        return getFileName(input);
      }

      if (
        input.matches('select')
      ) {
        if (!input.value) {
          return '';
        }

        const selectedOption =
          input.options[
            input.selectedIndex
          ];

        return selectedOption
          ? selectedOption
              .textContent
              .trim()
          : input.value;
      }

      const selectedChoice =
        field.querySelector(
          '[data-cpa-choice][aria-pressed="true"]'
        );

      if (selectedChoice) {
        return (
          selectedChoice.dataset
            .label ||
          selectedChoice
            .textContent
            .trim()
        );
      }

      if (
        input.matches(
          'input, textarea'
        )
      ) {
        return input.value || '';
      }

      return '';
    }

    function getFieldRawValue(
      panel,
      fieldKey
    ) {
      const field =
        resolveField(
          panel,
          fieldKey
        );

      return getFieldRawValueFromField(
        field
      );
    }

    function getFieldDisplayValue(
      panel,
      fieldKey
    ) {
      const field =
        resolveField(
          panel,
          fieldKey
        );

      return getFieldDisplayValueFromField(
        field
      );
    }

    function getSelectedOptionAddonVariantId(
      panel,
      fieldKey
    ) {
      const field =
        resolveField(
          panel,
          fieldKey
        );

      const input =
        getFieldInput(field);

      if (
        !field ||
        field.hidden ||
        !input ||
        input.disabled
      ) {
        return '';
      }

      if (
        input.matches('select')
      ) {
        if (!input.value) {
          return '';
        }

        const selectedOption =
          input.options[
            input.selectedIndex
          ];

        return (
          (selectedOption &&
            selectedOption.dataset
              .optionAddonVariantId) ||
          ''
        );
      }

      const selectedChoice =
        field.querySelector(
          '[data-cpa-choice][aria-pressed="true"]'
        );

      if (selectedChoice) {
        return (
          selectedChoice.dataset
            .optionAddonVariantId ||
          ''
        );
      }

      return '';
    }

    function getSelectedOptionAddonPriceCents(
      panel,
      fieldKey
    ) {
      const field =
        resolveField(
          panel,
          fieldKey
        );

      const input =
        getFieldInput(field);

      if (
        !field ||
        field.hidden ||
        !input ||
        input.disabled
      ) {
        return 0;
      }

      if (
        input.matches('select')
      ) {
        if (!input.value) {
          return 0;
        }

        const selectedOption =
          input.options[
            input.selectedIndex
          ];

        return parseCents(
          selectedOption &&
            selectedOption.dataset
              .optionAddonPrice
        );
      }

      const selectedChoice =
        field.querySelector(
          '[data-cpa-choice][aria-pressed="true"]'
        );

      if (selectedChoice) {
        return parseCents(
          selectedChoice.dataset
            .optionAddonPrice
        );
      }

      return 0;
    }

    function getSelectedOptionElement(
  field
) {
  if (
    !field ||
    field.hidden
  ) {
    return null;
  }

  const input =
    getFieldInput(field);

  if (
    !input ||
    input.disabled
  ) {
    return null;
  }

  if (
    input.matches('select')
  ) {
    if (!input.value) {
      return null;
    }

    return (
      input.options[
        input.selectedIndex
      ] || null
    );
  }

  return field.querySelector(
    '[data-cpa-choice][aria-pressed="true"]'
  );
}

function getFieldOneTimeAddon(
  field
) {
  const selectedOption =
    getSelectedOptionElement(
      field
    );

  if (!selectedOption) {
    return null;
  }

  const variantId =
    Number(
      selectedOption.dataset
        .optionOneTimeAddonVariantId ||
        0
    );

  if (
    !Number.isFinite(
      variantId
    ) ||
    variantId < 1
  ) {
    return null;
  }

  return {
    variantId,
    price: parseCents(
      selectedOption.dataset
        .optionOneTimeAddonPrice
    ),
    compareAtPrice: parseCents(
      selectedOption.dataset
        .optionOneTimeAddonCompareAtPrice
    ),
    title:
      String(
        selectedOption.dataset
          .optionOneTimeAddonTitle ||
          'One-Time Customization'
      ).trim(),
    optionLabel:
      String(
        selectedOption.dataset
          .label ||
          selectedOption.dataset
            .optionLabel ||
          selectedOption.textContent ||
          ''
      ).trim()
  };
}

function getGroupOneTimeAddons(
  group
) {
  if (!group) {
    return [];
  }

  const addons = [];
  const seenVariantIds =
    new Set();

  const fields =
    Array.from(
      group.querySelectorAll(
        '[data-cpa-field]'
      )
    ).filter(
      function (field) {
        return !field.hidden;
      }
    );

  fields.forEach(
    function (field) {
      const addon =
        getFieldOneTimeAddon(
          field
        );

      if (
        !addon ||
        seenVariantIds.has(
          addon.variantId
        )
      ) {
        return;
      }

      seenVariantIds.add(
        addon.variantId
      );

      addons.push({
        ...addon,
        fieldKey:
          field.dataset
            .fieldKey || '',
        propertyName:
          field.dataset
            .propertyName || '',
        fieldValue:
          getFieldDisplayValueFromField(
            field
          )
      });
    }
  );

  return addons;
}

function getAllOneTimeAddons() {
  const addons = [];
  const seenVariantIds =
    new Set();

  groups.forEach(
    function (group) {
      if (
        !groupHasCustomizationData(
          group
        )
      ) {
        return;
      }

      getGroupOneTimeAddons(
        group
      ).forEach(
        function (addon) {
          if (
            seenVariantIds.has(
              addon.variantId
            )
          ) {
            return;
          }

          seenVariantIds.add(
            addon.variantId
          );

          addons.push({
            ...addon,
            group
          });
        }
      );
    }
  );

  return addons;
}

function getOneTimeAddonTotalCents() {
  return getAllOneTimeAddons()
    .reduce(
      function (
        total,
        addon
      ) {
        return (
          total +
          addon.price
        );
      },
      0
    );
}

    function getSelectedOptionImage(
      field
    ) {
      if (!field) {
        return {
          image: '',
          label: ''
        };
      }

      const select =
        field.querySelector(
          'select[data-cpa-input]'
        );

      if (
        select &&
        select.value
      ) {
        const selectedOption =
          select.options[
            select.selectedIndex
          ];

        return {
          image:
            (selectedOption &&
              selectedOption.dataset
                .optionImage) ||
            '',
          label:
            (selectedOption &&
              (selectedOption.dataset
                .optionLabel ||
                selectedOption
                  .textContent
                  .trim())) ||
            ''
        };
      }

      const selectedChoice =
        field.querySelector(
          '[data-cpa-choice][aria-pressed="true"]'
        );

      if (selectedChoice) {
        return {
          image:
            selectedChoice.dataset
              .optionImage ||
            '',
          label:
            selectedChoice.dataset
              .label ||
            selectedChoice
              .textContent
              .trim() ||
            ''
        };
      }

      return {
        image: '',
        label: ''
      };
    }

    function getSelectedOptionHelpText(
      field
    ) {
      if (!field) return '';

      const select =
        field.querySelector(
          'select[data-cpa-input]'
        );

      if (
        select &&
        select.value
      ) {
        const selectedOption =
          select.options[
            select.selectedIndex
          ];

        return (
          (selectedOption &&
            selectedOption.dataset
              .optionHelpText) ||
          ''
        );
      }

      const selectedChoice =
        field.querySelector(
          '[data-cpa-choice][aria-pressed="true"]'
        );

      if (selectedChoice) {
        return (
          selectedChoice.dataset
            .optionHelpText ||
          ''
        );
      }

      return '';
    }

    function conditionPasses(
      field,
      panel
    ) {
      const enabled =
        normalizeBoolean(
          field.dataset
            .conditionEnabled
        );

      if (!enabled) {
        return true;
      }

      const sourceFieldKey =
        field.dataset
          .conditionSourceFieldKey ||
        '';

      const operator =
        field.dataset
          .conditionOperator ||
        '';

      const conditionValue =
        field.dataset
          .conditionValue ||
        '';

      if (
        !sourceFieldKey ||
        !operator
      ) {
        return true;
      }

      const sourceField =
        resolveConditionSourceField(
          field,
          panel,
          sourceFieldKey
        );

      const sourceRawValue =
        getFieldRawValueFromField(
          sourceField
        );

      const sourceDisplayValue =
        getFieldDisplayValueFromField(
          sourceField
        );

      const normalizedSourceRaw =
        normalizeValue(
          sourceRawValue
        );

      const normalizedSourceDisplay =
        normalizeValue(
          sourceDisplayValue
        );

      const normalizedCondition =
        normalizeValue(
          conditionValue
        );

      switch (operator) {
        case 'equals':
          return (
            normalizedSourceRaw ===
              normalizedCondition ||
            normalizedSourceDisplay ===
              normalizedCondition
          );

        case 'does_not_equal':
          return (
            normalizedSourceRaw !==
              normalizedCondition &&
            normalizedSourceDisplay !==
              normalizedCondition
          );

        case 'is_not_empty':
          return (
            sourceRawValue !== '' ||
            sourceDisplayValue !== ''
          );

        case 'is_empty':
          return (
            sourceRawValue === '' &&
            sourceDisplayValue === ''
          );

        default:
          return true;
      }
    }

    function ensureFieldError(
      field
    ) {
      let error =
        field.querySelector(
          '[data-cpa-field-error]'
        );

      if (!error) {
        error =
          document.createElement(
            'div'
          );

        error.className =
          'CPA__FieldError';

        error.dataset.cpaFieldError =
          '';

        error.textContent =
          'Please complete this field.';

        field.appendChild(error);
      }

      return error;
    }

    function setFieldInvalid(
      field,
      message
    ) {
      if (!field) return;

      const error =
        ensureFieldError(field);

      field.classList.add(
        'is-invalid'
      );

      error.textContent =
        message ||
        'Please complete this field.';
    }

    function clearFieldInvalid(
      field
    ) {
      if (!field) return;

      field.classList.remove(
        'is-invalid'
      );

      const error =
        field.querySelector(
          '[data-cpa-field-error]'
        );

      if (error) {
        error.textContent = '';
      }
    }

    function clearAllInvalidStates() {
      root
        .querySelectorAll(
          '[data-cpa-field].is-invalid'
        )
        .forEach(
          function (field) {
            clearFieldInvalid(
              field
            );
          }
        );
    }

    function clearFieldInvalidIfComplete(
      field
    ) {
      if (
        !field ||
        !field.classList.contains(
          'is-invalid'
        )
      ) {
        return;
      }

      const value =
        getFieldRawValueFromField(
          field
        );

      if (value) {
        clearFieldInvalid(field);
      }
    }

    function focusField(field) {
      if (!field) return;

      const input =
        field.querySelector(
          '[data-cpa-input]:not([disabled])'
        );

      const choice =
        field.querySelector(
          '[data-cpa-choice]'
        );

      window.setTimeout(
        function () {
          if (
            input &&
            typeof input.focus ===
              'function'
          ) {
            input.focus({
              preventScroll: true
            });

            return;
          }

          if (
            choice &&
            typeof choice.focus ===
              'function'
          ) {
            choice.focus({
              preventScroll: true
            });
          }
        },
        280
      );
    }

        function getPanelAddonPriceCents(
      group,
      panel
    ) {
      const addonMode =
        group.dataset.addonMode ||
        '';

      const pricingSourceFieldKey =
        group.dataset
          .pricingSourceFieldKey ||
        '';

      const groupAddonPrice =
        parseCents(
          group.dataset.addonPrice
        );

      if (
        !panelHasCustomizationData(
          panel
        )
      ) {
        return 0;
      }

      if (
        addonMode ===
        'once_per_application'
      ) {
        return groupAddonPrice;
      }

      if (
        addonMode ===
        'once_per_group'
      ) {
        return 0;
      }

      if (
        addonMode ===
        'quantity_from_field'
      ) {
        const sourceValue =
          parseInt(
            getFieldRawValue(
              panel,
              pricingSourceFieldKey
            ),
            10
          );

        if (
          !Number.isFinite(
            sourceValue
          ) ||
          sourceValue < 1
        ) {
          return 0;
        }

        return (
          groupAddonPrice *
          sourceValue
        );
      }

      if (
        addonMode ===
        'variant_from_application'
      ) {
        const applicationButton =
          group.querySelector(
            `[data-cpa-application-toggle][data-application-key="${escapeSelector(
              panel.dataset
                .applicationKey ||
                ''
            )}"]`
          );

        return parseCents(
          applicationButton &&
            applicationButton.dataset
              .applicationAddonPrice
        );
      }

      if (
        addonMode ===
        'variant_from_option'
      ) {
        return getSelectedOptionAddonPriceCents(
          panel,
          pricingSourceFieldKey
        );
      }

      return 0;
    }

    function getGroupLevelAddonPriceCents(
      group
    ) {
      const addonMode =
        group.dataset.addonMode ||
        '';

      const groupAddonPrice =
        parseCents(
          group.dataset.addonPrice
        );

      if (
        addonMode !==
        'once_per_group'
      ) {
        return 0;
      }

      if (
        !groupHasCustomizationData(
          group
        )
      ) {
        return 0;
      }

      return groupAddonPrice;
    }

    function updateOptionPreviewTriggerForField(
  field
) {
  const trigger =
    field.querySelector(
      '[data-cpa-option-preview-trigger]'
    );

  if (!trigger) return;

  const selected =
    getSelectedOptionImage(
      field
    );

  const hasImage =
    Boolean(
      selected.image &&
      String(
        selected.image
      ).trim()
    );

  if (
    hasImage &&
    !field.hidden
  ) {
    trigger.hidden = false;

    trigger.dataset.previewImage =
      selected.image;

    trigger.dataset.previewLabel =
      selected.label ||
      'Option example';

    trigger.setAttribute(
      'aria-label',
      selected.label
        ? `View ${selected.label} example`
        : 'View option example'
    );
  } else {
    trigger.hidden = true;

    trigger.dataset.previewImage =
      '';

    trigger.dataset.previewLabel =
      '';
  }
}

    function updateOptionInfoTriggerForField(
      field
    ) {
      const trigger =
        field.querySelector(
          '[data-cpa-option-info-trigger]'
        );

      if (!trigger) return;

      const helpText =
        getSelectedOptionHelpText(
          field
        );

      const selected =
        getSelectedOptionImage(
          field
        );

      if (
  helpText &&
  !field.hidden
) {
        trigger.hidden = false;

        trigger.dataset.infoText =
          helpText;

        trigger.dataset.infoLabel =
          selected.label ||
          'Option information';

        trigger.setAttribute(
          'aria-label',
          selected.label
            ? `View ${selected.label} information`
            : 'View option information'
        );
      } else {
        trigger.hidden = true;

        trigger.dataset.infoText =
          '';

        trigger.dataset.infoLabel =
          '';
      }
    }

    function updateOptionMetaRowForField(
      field
    ) {
      const metaRow =
        field.querySelector(
          '[data-cpa-option-meta-row]'
        );

      const infoTrigger =
        field.querySelector(
          '[data-cpa-option-info-trigger]'
        );

      const previewTrigger =
        field.querySelector(
          '[data-cpa-option-preview-trigger]'
        );

      if (!metaRow) return;

      const hasInfo =
        Boolean(
          infoTrigger &&
            !infoTrigger.hidden
        );

      const hasPreview =
        Boolean(
          previewTrigger &&
            !previewTrigger.hidden
        );

      metaRow.hidden =
  field.hidden ||
  (
    !hasInfo &&
    !hasPreview
  );
    }
    function updateOptionMetaActions() {
      root
        .querySelectorAll(
          '[data-cpa-field]'
        )
        .forEach(
          function (field) {
            updateOptionPreviewTriggerForField(
              field
            );

            updateOptionInfoTriggerForField(
              field
            );

            updateOptionMetaRowForField(
              field
            );
          }
        );
    }

    function getPreviewModal() {
      if (
        previewModal &&
        previewModal.parentElement !==
          document.body
      ) {
        document.body.appendChild(
          previewModal
        );
      }

      return previewModal;
    }

    function openPreviewModal(
      imageUrl,
      label
    ) {
      const modal =
        getPreviewModal();

      if (
        !modal ||
        !imageUrl
      ) {
        return;
      }

      const image =
        modal.querySelector(
          '[data-cpa-preview-image]'
        );

      const title =
        modal.querySelector(
          '[data-cpa-preview-title]'
        );

      const cleanLabel =
        String(label || '').trim();

      if (!image) return;

      image.src = imageUrl;

      image.alt =
        cleanLabel ||
        'Customization example';

      if (title) {
        if (cleanLabel) {
          title.textContent =
            cleanLabel;

          title.hidden = false;
        } else {
          title.textContent = '';
          title.hidden = true;
        }
      }

      modal.hidden = false;

      document.documentElement.classList.add(
        'CPA-PreviewModalOpen'
      );
    }

    function closePreviewModal() {
      const modal =
        previewModal;

      const image =
        modal
          ? modal.querySelector(
              '[data-cpa-preview-image]'
            )
          : null;

      const title =
        modal
          ? modal.querySelector(
              '[data-cpa-preview-title]'
            )
          : null;

      if (!modal) return;

      modal.hidden = true;

      if (image) {
        image.src = '';
        image.alt = '';
      }

      if (title) {
        title.textContent = '';
        title.hidden = false;
      }

      document.documentElement.classList.remove(
        'CPA-PreviewModalOpen'
      );
    }

    function getInfoModal() {
      if (
        infoModal &&
        infoModal.parentElement !==
          document.body
      ) {
        document.body.appendChild(
          infoModal
        );
      }

      return infoModal;
    }

    function openInfoModal(
      infoText,
      label
    ) {
      const modal =
        getInfoModal();

      if (
        !modal ||
        !infoText
      ) {
        return;
      }

      const title =
        modal.querySelector(
          '[data-cpa-info-title]'
        );

      const body =
        modal.querySelector(
          '[data-cpa-info-body]'
        );

      if (title) {
        title.textContent =
          label ||
          'Option information';
      }

      if (body) {
        body.textContent =
          infoText;
      }

      modal.hidden = false;

      document.documentElement.classList.add(
        'CPA-PreviewModalOpen'
      );
    }

    function closeInfoModal() {
      const modal =
        infoModal;

      if (!modal) return;

      const title =
        modal.querySelector(
          '[data-cpa-info-title]'
        );

      const body =
        modal.querySelector(
          '[data-cpa-info-body]'
        );

      modal.hidden = true;

      if (title) {
        title.textContent = '';
      }

      if (body) {
        body.textContent = '';
      }

      document.documentElement.classList.remove(
        'CPA-PreviewModalOpen'
      );
    }

        function clearField(
      field,
      preserveAutomaticSelection
    ) {
      if (!field) return;

      const shouldPreserveAutomaticSelection =
        preserveAutomaticSelection !== false &&
        isAutoSelectedField(field);

      field
        .querySelectorAll(
          '[data-cpa-choice]'
        )
        .forEach(function (choice) {
          choice.setAttribute(
            'aria-pressed',
            'false'
          );

          choice.classList.remove(
            'is-selected'
          );
        });

      field
        .querySelectorAll(
          '[data-cpa-choice-value]'
        )
        .forEach(function (input) {
          input.value = '';
        });

      const standardInput =
        field.querySelector(
          '[data-cpa-input]:not([data-cpa-choice-value])'
        );

      if (standardInput) {
        if (isFileInput(standardInput)) {
          standardInput.value = '';
        } else if (
          standardInput.matches(
            'input, textarea, select'
          )
        ) {
          standardInput.value = '';
        }
      }

      const swatchLabel =
        field.querySelector(
          '[data-cpa-swatch-label]'
        );

      if (swatchLabel) {
        swatchLabel.textContent =
          'Select a color';
      }

      if (
        shouldPreserveAutomaticSelection
      ) {
        restoreAutoSelectedField(
          field
        );
      } else {
        clearFieldInvalid(field);
      }

      updateOptionMetaActions();
    }

    function clearSetupPanel(
      setupPanel
    ) {
      if (!setupPanel) return;

      setupPanel
        .querySelectorAll(
          '[data-cpa-field]'
        )
        .forEach(function (field) {
          clearField(field, true);
        });
    }

    function clearApplicationPanel(
      panel
    ) {
      if (!panel) return;

      panel
        .querySelectorAll(
          '[data-cpa-field]'
        )
        .forEach(function (field) {
          clearField(field, true);
        });
    }

    function clearGroupFields(
      group
    ) {
      if (!group) return;

      const setupPanel =
        getSetupPanel(group);

      if (setupPanel) {
        clearSetupPanel(setupPanel);
      }

      group
        .querySelectorAll(
          '[data-cpa-application-panel]'
        )
        .forEach(function (panel) {
          clearApplicationPanel(panel);
        });
    }

    function updateConditionalFields(
      panel
    ) {
      if (!panel) return;

      const fields =
        Array.from(
          panel.querySelectorAll(
            '[data-cpa-field]'
          )
        );

      fields.forEach(
        function (field) {
          const visible =
            conditionPasses(
              field,
              panel
            );

          const inputs =
            Array.from(
              field.querySelectorAll(
                '[data-cpa-input]'
              )
            );

          const wasHidden =
            field.hidden;

          field.hidden =
            !visible;

          inputs.forEach(
            function (input) {
              input.disabled =
                !visible;
            }
          );

          if (!visible) {
            clearField(
              field,
              false
            );
          } else if (
            isAutoSelectedField(
              field
            )
          ) {
            restoreAutoSelectedField(
              field
            );

            inputs.forEach(
              function (input) {
                input.disabled =
                  false;
              }
            );
          } else if (wasHidden) {
            clearFieldInvalid(field);
          }
        }
      );

      updateOptionMetaActions();
    }

    function updateSetupConditions(
      group
    ) {
      if (!group) return;

      const setupPanel =
        getSetupPanel(group);

      if (!setupPanel) return;

      updateConditionalFields(
        setupPanel
      );
    }

    function updateApplicationConditions(
      group
    ) {
      if (!group) return;

      getActiveApplicationPanels(
        group
      ).forEach(
        function (panel) {
          updateConditionalFields(
            panel
          );
        }
      );
    }

    function updateAllConditions() {
      groups.forEach(
        function (group) {
          updateSetupConditions(
            group
          );

          updateApplicationConditions(
            group
          );
        }
      );
    }

    function updateApplicationButtons(
      group
    ) {
      if (!group) return;

      const buttons =
        Array.from(
          group.querySelectorAll(
            '[data-cpa-application-toggle]'
          )
        );

      buttons.forEach(
        function (button) {
          const key =
            button.dataset
              .applicationKey;

          const panel =
            group.querySelector(
              `[data-cpa-application-panel][data-application-key="${escapeSelector(
                key
              )}"]`
            );

          const active =
            Boolean(
              panel &&
                !panel.hidden
            );

          button.setAttribute(
            'aria-pressed',
            active
              ? 'true'
              : 'false'
          );

          button.classList.toggle(
            'is-selected',
            active
          );
        }
      );
    }

    function removeApplicationSelection(
      groupKey,
      applicationKey
    ) {
      const group =
        root.querySelector(
          `[data-cpa-group][data-group-key="${escapeSelector(
            groupKey
          )}"]`
        );

      if (!group) return;

      const panel =
        group.querySelector(
          `[data-cpa-application-panel][data-application-key="${escapeSelector(
            applicationKey
          )}"]`
        );

      if (!panel) return;

      const summaryApplication =
        root.querySelector(
          `[data-cpa-summary-application][data-group-key="${escapeSelector(
            groupKey
          )}"][data-application-key="${escapeSelector(
            applicationKey
          )}"]`
        );

      if (summaryApplication) {
        const summaryGroup =
          summaryApplication.closest(
            '[data-cpa-summary-group]'
          );

        const applicationCount =
          summaryGroup
            ? summaryGroup.querySelectorAll(
                '[data-cpa-summary-application]'
              ).length
            : 0;

        if (
          summaryGroup &&
          applicationCount <= 1
        ) {
          summaryGroup.classList.add(
            'is-removing'
          );
        } else {
          summaryApplication.classList.add(
            'is-removing'
          );
        }
      }

      waitForSummaryAnimation(
        function () {
          const allowMultiple =
            normalizeBoolean(
              group.dataset
                .allowMultipleApplications
            );

          clearApplicationPanel(
            panel
          );

          if (allowMultiple) {
            panel.hidden = true;
          } else {
            panel.hidden = false;
          }

          updateApplicationButtons(
  group
);

updateAllConditions();
updateSummary();
updateOptionMetaActions();

if (
  context === 'product'
) {
  setCpaAccordionOpen(
    true
  );

  scrollToCpaPanelIfNeeded();
}

        },
        240
      );
    }

    function handleApplicationToggle(
      button
    ) {
      const group =
        button.closest(
          '[data-cpa-group]'
        );

      if (!group) return;

      const allowMultiple =
        normalizeBoolean(
          group.dataset
            .allowMultipleApplications
        );

      const key =
        button.dataset
          .applicationKey;

      const panels =
        Array.from(
          group.querySelectorAll(
            '[data-cpa-application-panel]'
          )
        );

      const targetPanel =
        group.querySelector(
          `[data-cpa-application-panel][data-application-key="${escapeSelector(
            key
          )}"]`
        );

      if (!targetPanel) return;

      if (allowMultiple) {
        targetPanel.hidden =
          !targetPanel.hidden;

        if (targetPanel.hidden) {
          clearApplicationPanel(
            targetPanel
          );
        } else {
          targetPanel
            .querySelectorAll(
              '[data-cpa-field][data-cpa-auto-selected="true"]'
            )
            .forEach(
              function (field) {
                restoreAutoSelectedField(
                  field
                );
              }
            );
        }
      } else {
        panels.forEach(
          function (panel) {
            const shouldHide =
              panel !==
              targetPanel;

            panel.hidden =
              shouldHide;

            if (shouldHide) {
              clearApplicationPanel(
                panel
              );
            } else {
              panel
                .querySelectorAll(
                  '[data-cpa-field][data-cpa-auto-selected="true"]'
                )
                .forEach(
                  function (field) {
                    restoreAutoSelectedField(
                      field
                    );
                  }
                );
            }
          }
        );
      }

      updateApplicationButtons(
        group
      );

      updateAllConditions();
      updateSummary();
      updateOptionMetaActions();
    }

    function handleChoiceClick(
  choice
) {
  const field =
    choice.closest(
      '[data-cpa-field]'
    );

  if (!field) return;

  const choices =
    Array.from(
      field.querySelectorAll(
        '[data-cpa-choice]'
      )
    );

  const hiddenInput =
    field.querySelector(
      '[data-cpa-choice-value]'
    );

  const isSwatch =
    choice.classList.contains(
      'CPA__Swatch'
    );

  const wasSelected =
    choice.getAttribute(
      'aria-pressed'
    ) === 'true';

  const isRequired =
    normalizeBoolean(
      field.dataset.required
    );

  const allowDeselect =
    !isRequired &&
    !isAutoSelectedField(
      field
    );

  choices.forEach(
    function (item) {
      item.setAttribute(
        'aria-pressed',
        'false'
      );

      item.classList.remove(
        'is-selected'
      );
    }
  );

  if (
    wasSelected &&
    allowDeselect
  ) {
    if (hiddenInput) {
      hiddenInput.value = '';
    }

    if (isSwatch) {
      const swatchLabel =
        field.querySelector(
          '[data-cpa-swatch-label]'
        );

      if (swatchLabel) {
        swatchLabel.textContent =
          'Select a color';
      }
    }

    clearFieldInvalid(field);

    updateAllConditions();
    updateSummary();
    updateOptionMetaActions();

    return;
  }

  choice.setAttribute(
    'aria-pressed',
    'true'
  );

  choice.classList.add(
    'is-selected'
  );

  if (hiddenInput) {
    hiddenInput.value =
      choice.dataset.value ||
      '';
  }

  if (isSwatch) {
    const swatchLabel =
      field.querySelector(
        '[data-cpa-swatch-label]'
      );

    if (swatchLabel) {
      swatchLabel.textContent =
        choice.dataset.label ||
        'Selected';
    }
  }

  clearFieldInvalid(field);

  updateAllConditions();
  updateSummary();
  updateOptionMetaActions();
}

        function getFieldsSummary(
      panel
    ) {
      if (!panel) return [];

      const fields =
        Array.from(
          panel.querySelectorAll(
            '[data-cpa-field]'
          )
        ).filter(
          function (field) {
            return !field.hidden;
          }
        );

      const fieldLines = [];

      fields.forEach(
        function (field) {
          const propertyName =
            field.dataset
              .propertyName;

          if (!propertyName) {
            return;
          }

          const value =
            getFieldDisplayValueFromField(
              field
            );

          if (!value) return;

          fieldLines.push({
            propertyName,
            value,
            fieldKey:
              field.dataset
                .fieldKey || '',
            fieldScope:
              getFieldScope(field),
            isAutomatic:
              isAutoSelectedField(
                field
              )
          });
        }
      );

      return fieldLines;
    }

    function getSetupSummary(
      group
    ) {
      return getFieldsSummary(
        getSetupPanel(group)
      );
    }

    function getPanelSummary(
      panel
    ) {
      return getFieldsSummary(
        panel
      );
    }

    function panelHasCustomizationData(
      panel
    ) {
      if (!panel) return false;

      const fields =
        Array.from(
          panel.querySelectorAll(
            '[data-cpa-field]'
          )
        ).filter(
          function (field) {
            return (
              !field.hidden &&
              !isAutoSelectedField(
                field
              )
            );
          }
        );

      return fields.some(
        function (field) {
          return Boolean(
            String(
              getFieldRawValueFromField(
                field
              ) || ''
            ).trim()
          );
        }
      );
    }

    function setupHasCustomizationData(
      group
    ) {
      const setupPanel =
        getSetupPanel(group);

      return panelHasCustomizationData(
        setupPanel
      );
    }

    function groupHasApplicationData(
  group
) {
  if (!group) {
    return false;
  }

  return getActiveApplicationPanels(
    group
  ).some(
    function (panel) {
      return panelIsSelected(
        panel
      );
    }
  );
}

    function groupHasCustomizationData(
      group
    ) {
      if (!group) return false;

      return (
        setupHasCustomizationData(
          group
        ) ||
        groupHasApplicationData(
          group
        )
      );
    }

    function hasCustomizationData() {
      return groups.some(
        function (group) {
          return groupHasCustomizationData(
            group
          );
        }
      );
    }

    function getGroupNotes(group) {
      if (!group) {
        return {
          propertyName: '',
          privatePropertyName: '',
          value: ''
        };
      }

      const notesWrapper =
        group.querySelector(
          '[data-cpa-group-notes]'
        );

      const notesInput =
        group.querySelector(
          '[data-cpa-group-notes-input]'
        );

      if (!notesInput) {
        return {
          propertyName: '',
          privatePropertyName: '',
          value: ''
        };
      }

      const propertyName =
        notesInput.dataset
          .propertyName ||
        (notesWrapper &&
          notesWrapper.dataset
            .propertyName) ||
        'Additional Notes';

      return {
        propertyName,
        privatePropertyName:
          `_CPA Note: ${propertyName}`,
        value: String(
          notesInput.value || ''
        ).trim()
      };
    }

    function updateGroupNotesVisibility(
      group
    ) {
      if (!group) return;

      const notesPanel =
        group.querySelector(
          '[data-cpa-group-notes]'
        );

      if (!notesPanel) return;

      notesPanel.hidden =
        !groupHasCustomizationData(
          group
        );
    }

    function updateAllGroupNotesVisibility() {
      groups.forEach(
        function (group) {
          updateGroupNotesVisibility(
            group
          );
        }
      );
    }

    function renderSummaryFieldLines(
      fieldLines
    ) {
      if (
        !Array.isArray(
          fieldLines
        ) ||
        !fieldLines.length
      ) {
        return '';
      }

      return fieldLines
        .map(
          function (line) {
            return `
              <div class="CPA__SummaryField">
                <span class="CPA__SummaryFieldName">
                  ${escapeHtml(
                    line.propertyName
                  )}:
                </span>

                <span class="CPA__SummaryFieldValue">
                  ${escapeHtml(
                    line.value
                  )}
                </span>
              </div>
            `;
          }
        )
        .join('');
    }

    function updateSummary() {
      updateAllGroupNotesVisibility();

      if (!summaryContent) {
        return;
      }

      const summaryWrapper =
        root.querySelector(
          '[data-cpa-summary]'
        );

      const selectedGroups = [];

      const isBulkContext =
        context === 'bulk';

      const mainQuantity =
        isBulkContext
          ? getBulkSelectionQuantity()
          : findMainQuantity(
              productForm
            );

      const mainProductPrice =
        isBulkContext
          ? 0
          : getMainProductPriceCents();

      const bulkProductTotal =
        isBulkContext
          ? getBulkSelectionProductTotal()
          : 0;

      let customizationTotal = 0;

const oneTimeAddonTotal =
  getOneTimeAddonTotalCents();

      groups.forEach(
        function (group) {
          if (
            !groupHasCustomizationData(
              group
            )
          ) {
            return;
          }

          const groupTitleElement =
            group.querySelector(
              '.CPA__GroupTitle'
            );

          const groupTitle =
            groupTitleElement
              ? groupTitleElement
                  .textContent
                  .trim()
              : 'Customization';

          const groupKey =
            group.dataset
              .groupKey || '';

          const setupFieldLines =
            getSetupSummary(group);

          const activePanels =
            getActiveApplicationPanels(
              group
            );

          const selectedApplications =
            [];

          const groupLevelAddonPrice =
            getGroupLevelAddonPriceCents(
              group
            );

          const groupNotes =
            getGroupNotes(group);

          activePanels.forEach(
            function (panel) {
              if (
                !panelHasCustomizationData(
                  panel
                )
              ) {
                return;
              }

              const applicationTitleElement =
                panel.querySelector(
                  '.CPA__ApplicationTitle'
                );

              const applicationTitle =
                applicationTitleElement
                  ? applicationTitleElement
                      .textContent
                      .trim()
                  : '';

              const applicationKey =
                panel.dataset
                  .applicationKey ||
                '';

              const fieldLines =
                getPanelSummary(
                  panel
                );

              const applicationAddonPrice =
                getPanelAddonPriceCents(
                  group,
                  panel
                );

              customizationTotal +=
                applicationAddonPrice;

              selectedApplications.push({
                applicationTitle,
                applicationKey,
                fieldLines,
                addonPrice:
                  applicationAddonPrice
              });
            }
          );

          if (
            groupLevelAddonPrice > 0
          ) {
            customizationTotal +=
              groupLevelAddonPrice;
          }

          selectedGroups.push({
            groupTitle,
            groupKey,
            groupLevelAddonPrice,
            setupFieldLines,
            notes:
              groupNotes.value,
            notesPropertyName:
              groupNotes.propertyName,
            applications:
              selectedApplications
          });
        }
      );

      if (!selectedGroups.length) {
        if (
          summaryWrapper &&
          !summaryWrapper.hidden
        ) {
          summaryWrapper.classList.add(
            'is-leaving'
          );

          window.setTimeout(
            function () {
              summaryContent.innerHTML =
                '';

              summaryWrapper.hidden =
                true;

              summaryWrapper.classList.remove(
                'is-leaving',
                'is-entering'
              );

        
            },
            240
          );
        } else {
          if (summaryWrapper) {
            summaryWrapper.hidden =
              true;

            summaryWrapper.classList.remove(
              'is-leaving',
              'is-entering'
            );
          }

          summaryContent.innerHTML =
            '';
        }

        return;
      }

      if (summaryWrapper) {
        const wasHidden =
          summaryWrapper.hidden;

        summaryWrapper.hidden =
          false;

        summaryWrapper.classList.remove(
          'is-leaving'
        );

        if (wasHidden) {
          summaryWrapper.classList.add(
            'is-entering'
          );

          window.setTimeout(
            function () {
              summaryWrapper.classList.remove(
                'is-entering'
              );
            },
            280
          );
        }
      }

      const unitTotal =
  mainProductPrice +
  customizationTotal;

const totalPrice =
  isBulkContext
    ? bulkProductTotal +
      customizationTotal *
        mainQuantity +
      oneTimeAddonTotal
    : unitTotal *
        mainQuantity +
      oneTimeAddonTotal;

      const totalNote =
        isBulkContext
          ? mainQuantity > 0
            ? `Includes customization across ${mainQuantity} ${
                mainQuantity === 1
                  ? 'product'
                  : 'products'
              }`
            : 'Enter bulk quantities to calculate the complete total'
          : 'Includes all customizations';

      summaryContent.innerHTML = `
        ${selectedGroups
          .map(
            function (group) {
              const hasSetupFields =
                group.setupFieldLines
                  .length > 0;

              const hasApplications =
                group.applications
                  .length > 0;

              return `
                <div
                  class="CPA__SummaryGroup"
                  data-cpa-summary-group="${escapeHtml(
                    group.groupKey
                  )}"
                >
                  <div class="CPA__SummaryGroupHeader">
                    <div class="CPA__SummaryGroupTitle">
                      ${escapeHtml(
                        group.groupTitle
                      )}
                    </div>

                    ${
                      group.groupLevelAddonPrice >
                      0
                        ? `
                          <div class="CPA__SummaryGroupPrice">
                            +${formatMoney(
                              group.groupLevelAddonPrice
                            )}
                          </div>
                        `
                        : ''
                    }
                  </div>

                  ${
                    hasSetupFields
                      ? `
                        <div
                          class="CPA__SummarySetup"
                          data-cpa-summary-setup
                        >
                          <div class="CPA__SummarySetupFields">
                            ${renderSummaryFieldLines(
                              group.setupFieldLines
                            )}
                          </div>
                        </div>
                      `
                      : ''
                  }

                  ${
                    hasApplications
                      ? `
                        <div class="CPA__SummaryApplications">
                          ${group.applications
                            .map(
                              function (
                                application
                              ) {
                                return `
                                  <div
                                    class="CPA__SummaryApplication"
                                    data-cpa-summary-application
                                    data-group-key="${escapeHtml(
                                      group.groupKey
                                    )}"
                                    data-application-key="${escapeHtml(
                                      application.applicationKey
                                    )}"
                                  >
                                    <div class="CPA__SummaryApplicationHeader">
                                      <div class="CPA__SummaryApplicationTitle">
                                        ${escapeHtml(
                                          application.applicationTitle
                                        )}
                                      </div>

                                      <div class="CPA__SummaryApplicationActions">
                                        ${
                                          application.addonPrice >
                                          0
                                            ? `
                                              <div class="CPA__SummaryApplicationPrice">
                                                +${formatMoney(
                                                  application.addonPrice
                                                )}
                                              </div>
                                            `
                                            : ''
                                        }

                                        <button
                                          type="button"
                                          class="CPA__SummaryRemove"
                                          data-cpa-summary-remove-application
                                          data-group-key="${escapeHtml(
                                            group.groupKey
                                          )}"
                                          data-application-key="${escapeHtml(
                                            application.applicationKey
                                          )}"
                                          aria-label="Remove ${escapeHtml(
                                            application.applicationTitle
                                          )} customization"
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    </div>

                                    <div class="CPA__SummaryFields">
                                      ${renderSummaryFieldLines(
                                        application.fieldLines
                                      )}
                                    </div>
                                  </div>
                                `;
                              }
                            )
                            .join('')}
                        </div>
                      `
                      : ''
                  }

                  ${
                    group.notes
                      ? `
                        <div class="CPA__SummaryGroupNotes">
                          <div class="CPA__SummaryGroupNotesLabel">
                            ${escapeHtml(
                              group.notesPropertyName
                            )}
                          </div>

                          <div class="CPA__SummaryGroupNotesValue">
                            ${escapeHtml(
                              group.notes
                            )}
                          </div>
                        </div>
                      `
                      : ''
                  }
                </div>
              `;
            }
          )
          .join('')}

          ${
  oneTimeAddonTotal > 0
    ? `
      <div class="CPA__SummaryOneTimeAddons">
        ${getAllOneTimeAddons()
          .map(
            function (addon) {
              return `
                <div class="CPA__SummaryOneTimeAddon">
                  <div class="CPA__SummaryOneTimeAddonTitle">
                    ${escapeHtml(
                      addon.title ||
                      'One-Time Customization'
                    )}
                  </div>

                  <div class="CPA__SummaryOneTimeAddonPrice">
                    +${formatMoney(
                      addon.price
                    )}
                  </div>
                </div>
              `;
            }
          )
          .join('')}
      </div>
    `
    : ''
}

        <div class="CPA__SummaryTotal">
          <div class="CPA__SummaryTotalCopy">
            <div class="CPA__SummaryTotalLabel">
              Total
            </div>

            <div class="CPA__SummaryTotalNote">
              ${escapeHtml(
                totalNote
              )}
            </div>
          </div>

          <div class="CPA__SummaryTotalPrice">
            ${formatMoney(
              totalPrice
            )}
          </div>
        </div>
      `;
    }

        function validatePanel(
      panel,
      shouldMarkInvalid
    ) {
      if (!panel) {
        return {
          valid: true,
          message: '',
          field: null
        };
      }

      const activeFields =
        Array.from(
          panel.querySelectorAll(
            '[data-cpa-field]'
          )
        ).filter(
          function (field) {
            return !field.hidden;
          }
        );

      let firstInvalidField =
        null;

      activeFields.forEach(
        function (field) {
          if (shouldMarkInvalid) {
            clearFieldInvalid(
              field
            );
          }
        }
      );

      for (
        const field of activeFields
      ) {
        const required =
          normalizeBoolean(
            field.dataset.required
          );

        if (!required) {
          continue;
        }

        const value =
          String(
            getFieldRawValueFromField(
              field
            ) || ''
          ).trim();

        if (!value) {
          if (shouldMarkInvalid) {
            setFieldInvalid(
              field,
              'Please complete this field.'
            );
          }

          if (
            !firstInvalidField
          ) {
            firstInvalidField =
              field;
          }
        }
      }

      if (firstInvalidField) {
        return {
          valid: false,
          message:
            'Please complete all required customization fields.',
          field:
            firstInvalidField
        };
      }

      return {
        valid: true,
        message: '',
        field: null
      };
    }

    function validateSetupPanel(
      group,
      shouldMarkInvalid
    ) {
      const setupPanel =
        getSetupPanel(group);

      if (!setupPanel) {
        return {
          valid: true,
          message: '',
          field: null
        };
      }

      return validatePanel(
        setupPanel,
        shouldMarkInvalid
      );
    }

    function validateApplicationPanels(
  group,
  shouldMarkInvalid
) {
  const activePanels =
    getActiveApplicationPanels(
      group
    );

  for (
    const panel of activePanels
  ) {
    if (
      !panelIsSelected(
        panel
      )
    ) {
      continue;
    }

    const validation =
      validatePanel(
        panel,
        shouldMarkInvalid
      );

    if (
      !validation.valid
    ) {
      return validation;
    }
  }

  return {
    valid: true,
    message: '',
    field: null
  };
}

    function panelIsSelected(
  panel
) {
  if (
    !panel ||
    panel.hidden
  ) {
    return false;
  }

  return true;
}

    function validateCustomization() {
      clearAllInvalidStates();

      if (
        !hasCustomizationData()
      ) {
        return {
          valid: true,
          message: '',
          field: null
        };
      }

      for (
        const group of groups
      ) {
        if (
          !groupHasCustomizationData(
            group
          )
        ) {
          continue;
        }

        const setupValidation =
          validateSetupPanel(
            group,
            true
          );

        if (
          !setupValidation.valid
        ) {
          return setupValidation;
        }

        const applicationValidation =
          validateApplicationPanels(
            group,
            true
          );

        if (
          !applicationValidation.valid
        ) {
          return applicationValidation;
        }
      }

      return {
        valid: true,
        message: '',
        field: null
      };
    }

    function getApplicationTitle(
      panel
    ) {
      if (!panel) return '';

      const title =
        panel.querySelector(
          '.CPA__ApplicationTitle'
        );

      return title
        ? title.textContent.trim()
        : '';
    }

    function getGroupTitle(group) {
      if (!group) {
        return 'Customization';
      }

      const title =
        group.querySelector(
          '.CPA__GroupTitle'
        );

      return title
        ? title.textContent.trim()
        : 'Customization';
    }

    function addPanelProperties(
      properties,
      panel
    ) {
      if (
        !properties ||
        !panel
      ) {
        return properties;
      }

      const fields =
        Array.from(
          panel.querySelectorAll(
            '[data-cpa-field]'
          )
        ).filter(
          function (field) {
            return !field.hidden;
          }
        );

      fields.forEach(
        function (field) {
          const propertyName =
            String(
              field.dataset
                .propertyName ||
              ''
            ).trim();

          if (!propertyName) {
            return;
          }

          const value =
            String(
              getFieldDisplayValueFromField(
                field
              ) || ''
            ).trim();

          if (!value) {
            return;
          }

          properties[
            propertyName
          ] = value;
        }
      );

      return properties;
    }

    function addSetupProperties(
      properties,
      group
    ) {
      return addPanelProperties(
        properties,
        getSetupPanel(group)
      );
    }

    function addApplicationProperties(
      properties,
      panel
    ) {
      return addPanelProperties(
        properties,
        panel
      );
    }

    function addAllSetupPropertiesToParent(
  properties
) {
  if (!properties) {
    return properties;
  }

  groups.forEach(
    function (group) {
      if (
        !groupHasCustomizationData(
          group
        )
      ) {
        return;
      }

      addSetupProperties(
        properties,
        group
      );
    }
  );

  /*
   * Keep Design Status on the nested add-on,
   * but remove it from the parent product.
   *
   * This recognizes:
   * Design Status
   * design-status
   * design_status
   * designstatus
   */
  Object.keys(
    properties
  ).forEach(
    function (propertyName) {
      const normalizedName =
        String(propertyName)
          .trim()
          .toLowerCase()
          .replace(
            /[\s_-]+/g,
            ''
          );

      if (
        normalizedName ===
        'designstatus'
      ) {
        delete properties[
          propertyName
        ];
      }
    }
  );

  return properties;
}

    function buildAddonProperties(
  group,
  panel,
  customizationId,
  bulkBuildId,
  quantityMultiplier
) {
  const groupTitle =
    getGroupTitle(group);

  const applicationTitle =
    panel
      ? getApplicationTitle(
          panel
        )
      : '';

  const parsedMultiplier =
    Number(
      quantityMultiplier
    );

  const safeMultiplier =
    Number.isFinite(
      parsedMultiplier
    ) &&
    parsedMultiplier > 0
      ? parsedMultiplier
      : 1;

  const properties = {
    '_Customization ID':
      customizationId,

    '_Customization Group Key':
      group.dataset.groupKey ||
      '',

    '_CPA Line Type':
      'add_on',

    '_Add-On Quantity Mode':
      'parent',

    '_Parent Quantity Multiplier':
      String(
        safeMultiplier
      ),

    'For Product':
      root.dataset
        .productTitle ||
      '',

    Customization:
      groupTitle
  };

  if (bulkBuildId) {
    properties[
      '_Bulk Build ID'
    ] = bulkBuildId;

    properties[
      '_Bulk Customization'
    ] = 'Yes';
  }

  addSetupProperties(
    properties,
    group
  );

  if (panel) {
    properties[
      '_Application Key'
    ] =
      panel.dataset
        .applicationKey ||
      '';

    if (applicationTitle) {
      properties.Application =
        applicationTitle;
    }

    addApplicationProperties(
      properties,
      panel
    );
  }

  return properties;
}

    function buildGroupAddonProperties(
  group,
  customizationId,
  bulkBuildId,
  quantityMultiplier
) {
  const groupTitle =
    getGroupTitle(group);

  const parsedMultiplier =
    Number(
      quantityMultiplier
    );

  const safeMultiplier =
    Number.isFinite(
      parsedMultiplier
    ) &&
    parsedMultiplier > 0
      ? parsedMultiplier
      : 1;

  const properties = {
    '_Customization ID':
      customizationId,

    '_Customization Group Key':
      group.dataset.groupKey ||
      '',

    '_CPA Line Type':
      'add_on',

    '_Add-On Quantity Mode':
      'parent',

    '_Parent Quantity Multiplier':
      String(
        safeMultiplier
      ),

    'For Product':
      root.dataset
        .productTitle ||
      '',

    Customization:
      groupTitle
  };

  if (bulkBuildId) {
    properties[
      '_Bulk Build ID'
    ] = bulkBuildId;

    properties[
      '_Bulk Customization'
    ] = 'Yes';
  }

  addSetupProperties(
    properties,
    group
  );

  const activePanels =
    getActiveApplicationPanels(
      group
    );

  const applicationSummaries =
    [];

  activePanels.forEach(
    function (panel) {
      if (
        !panelHasCustomizationData(
          panel
        )
      ) {
        return;
      }

      const applicationTitle =
        getApplicationTitle(
          panel
        );

      const fieldLines =
        getPanelSummary(
          panel
        );

      const fieldText =
        fieldLines
          .map(
            function (line) {
              return `${line.propertyName}: ${line.value}`;
            }
          )
          .join(', ');

      if (
        applicationTitle &&
        fieldText
      ) {
        applicationSummaries.push(
          `${applicationTitle} (${fieldText})`
        );
      } else if (
        applicationTitle
      ) {
        applicationSummaries.push(
          applicationTitle
        );
      } else if (
        fieldText
      ) {
        applicationSummaries.push(
          fieldText
        );
      }
    }
  );

  if (
    applicationSummaries.length
  ) {
    properties.Details =
      applicationSummaries.join(
        ' | '
      );
  }

  return properties;
}

    function buildOneTimeAddonProperties(
  group,
  addon,
  customizationId,
  bulkBuildId
) {
  const properties = {
    '_Customization ID':
      customizationId,

    '_Customization Group Key':
      group.dataset.groupKey ||
      '',

    '_CPA Line Type':
      'add_on',

    '_Add-On Quantity Mode':
      'one_time',

    '_One-Time Add-On':
      'Yes',

    'For Product':
      root.dataset
        .productTitle ||
      '',

    Customization:
      getGroupTitle(group),

    'Charge Type':
      addon.title ||
      'One-Time Customization'
  };

  if (
    addon.fieldValue
  ) {
    properties[
      addon.propertyName ||
      'Selected Option'
    ] =
      addon.fieldValue;
  }

  if (bulkBuildId) {
    properties[
      '_Bulk Build ID'
    ] = bulkBuildId;

    properties[
      '_Bulk Customization'
    ] = 'Yes';
  }

  return properties;
}

    function buildAddonLine(
      variantId,
      quantity,
      properties
    ) {
      const parsedVariantId =
        Number(variantId);

      const parsedQuantity =
        Number(quantity);

      if (
        !Number.isFinite(
          parsedVariantId
        ) ||
        parsedVariantId < 1 ||
        !Number.isFinite(
          parsedQuantity
        ) ||
        parsedQuantity < 1
      ) {
        return null;
      }

      return {
        id: parsedVariantId,
        quantity:
          parsedQuantity,
        properties
      };
    }

        function buildItemsForVariant(
      options
    ) {
      const settings =
        options || {};

        const includeOneTimeAddons =
  settings
    .includeOneTimeAddons !==
  false;

      const mainVariantId =
        Number(
          settings.variantId
        );

      const mainQuantity =
        Number(
          settings.quantity
        );

      const bulkBuildId =
        String(
          settings.bulkBuildId ||
            ''
        ).trim();

      const customizationId =
        String(
          settings.customizationId ||
            makeCustomizationId()
        ).trim();

      if (
        !Number.isFinite(
          mainVariantId
        ) ||
        mainVariantId < 1
      ) {
        return {
          valid: false,
          message:
            'Please select a product option before adding to cart.',
          items: [],
          field: null,
          customizationId,
          bulkBuildId
        };
      }

      if (
        !Number.isFinite(
          mainQuantity
        ) ||
        mainQuantity < 1
      ) {
        return {
          valid: false,
          message:
            'Please enter a valid product quantity.',
          items: [],
          field: null,
          customizationId,
          bulkBuildId
        };
      }

      if (
        !hasCustomizationData()
      ) {
        return {
          valid: true,
          message: '',
          items: [
            {
              id: mainVariantId,
              quantity:
                mainQuantity
            }
          ],
          field: null,
          customized: false,
          customizationId: '',
          bulkBuildId
        };
      }

      const validation =
        validateCustomization();

      if (!validation.valid) {
        return {
          valid: false,
          message:
            validation.message,
          items: [],
          field:
            validation.field,
          customizationId,
          bulkBuildId
        };
      }

      const parentProperties = {
  '_Customization ID':
    customizationId,

  '_Customized':
    'Yes',

  '_CPA Line Type':
    'parent'
};

      if (bulkBuildId) {
        parentProperties[
          '_Bulk Build ID'
        ] = bulkBuildId;

        parentProperties[
          '_Bulk Customization'
        ] = 'Yes';
      }

      addAllSetupPropertiesToParent(
        parentProperties
      );

      groups.forEach(
        function (group) {
          if (
            !groupHasCustomizationData(
              group
            )
          ) {
            return;
          }

          const groupNotes =
            getGroupNotes(group);

          if (
            groupNotes.value &&
            groupNotes
              .privatePropertyName
          ) {
            parentProperties[
              groupNotes
                .privatePropertyName
            ] =
              groupNotes.value;
          }
        }
      );

      const items = [
        {
          id: mainVariantId,
          quantity:
            mainQuantity,
          properties:
            parentProperties
        }
      ];

      for (
        const group of groups
      ) {
        if (
          !groupHasCustomizationData(
            group
          )
        ) {
          continue;
        }

        const addonMode =
          group.dataset
            .addonMode || '';

        const groupAddonVariantId =
          group.dataset
            .addonVariantId || '';

        const pricingSourceFieldKey =
          group.dataset
            .pricingSourceFieldKey ||
          '';

        const activePanels =
          getActiveApplicationPanels(
            group
          );

        const panelsWithData =
  activePanels.filter(
    function (panel) {
      return panelIsSelected(
        panel
      );
    }
  );

        if (
          addonMode ===
          'once_per_group'
        ) {
          if (
            !groupAddonVariantId
          ) {
            return {
              valid: false,
              message:
                'A customization add-on is missing its product variant.',
              items: [],
              field: null,
              customizationId,
              bulkBuildId
            };
          }

          const addonLine =
            buildAddonLine(
              groupAddonVariantId,
              mainQuantity,
              buildGroupAddonProperties(
                group,
                customizationId,
                bulkBuildId
              )
            );

          if (addonLine) {
            items.push(addonLine);
          }

          continue;
        }

        if (
          addonMode ===
          'once_per_application'
        ) {
          if (
            !panelsWithData.length
          ) {
            continue;
          }

          for (
            const panel of panelsWithData
          ) {
            if (
              !groupAddonVariantId
            ) {
              return {
                valid: false,
                message:
                  'A customization add-on is missing its product variant.',
                items: [],
                field: null,
                customizationId,
                bulkBuildId
              };
            }

            const addonLine =
              buildAddonLine(
                groupAddonVariantId,
                mainQuantity,
                buildAddonProperties(
                  group,
                  panel,
                  customizationId,
                  bulkBuildId
                )
              );

            if (addonLine) {
              items.push(
                addonLine
              );
            }
          }

          continue;
        }

        if (
          addonMode ===
          'quantity_from_field'
        ) {
          if (
            !panelsWithData.length
          ) {
            continue;
          }

          for (
            const panel of panelsWithData
          ) {
            if (
              !groupAddonVariantId
            ) {
              return {
                valid: false,
                message:
                  'A customization add-on is missing its product variant.',
                items: [],
                field: null,
                customizationId,
                bulkBuildId
              };
            }

            if (
              !pricingSourceFieldKey
            ) {
              return {
                valid: false,
                message:
                  'A customization add-on is missing its pricing source field.',
                items: [],
                field: null,
                customizationId,
                bulkBuildId
              };
            }

            const sourceValue =
              parseInt(
                getFieldRawValue(
                  panel,
                  pricingSourceFieldKey
                ),
                10
              );

            if (
              !Number.isFinite(
                sourceValue
              ) ||
              sourceValue < 1
            ) {
              const sourceField =
                resolveField(
                  panel,
                  pricingSourceFieldKey
                );

              if (sourceField) {
                setFieldInvalid(
                  sourceField,
                  'Please choose a valid customization quantity.'
                );
              }

              return {
                valid: false,
                message:
                  'Please choose a valid customization quantity.',
                items: [],
                field:
                  sourceField ||
                  null,
                customizationId,
                bulkBuildId
              };
            }

            const addonLine =
  buildAddonLine(
    groupAddonVariantId,
    mainQuantity *
      sourceValue,
    buildAddonProperties(
      group,
      panel,
      customizationId,
      bulkBuildId,
      sourceValue
    )
  );

            if (addonLine) {
              items.push(
                addonLine
              );
            }
          }

          continue;
        }

        if (
          addonMode ===
          'variant_from_application'
        ) {
          if (
            !panelsWithData.length
          ) {
            continue;
          }

          for (
            const panel of panelsWithData
          ) {
            const applicationButton =
              group.querySelector(
                `[data-cpa-application-toggle][data-application-key="${escapeSelector(
                  panel.dataset
                    .applicationKey ||
                    ''
                )}"]`
              );

            const applicationVariantId =
              applicationButton
                ? applicationButton
                    .dataset
                    .applicationAddonVariantId ||
                  ''
                : '';

            if (
              !applicationVariantId
            ) {
              return {
                valid: false,
                message:
                  'A selected customization application is missing its add-on variant.',
                items: [],
                field: null,
                customizationId,
                bulkBuildId
              };
            }

            const addonLine =
              buildAddonLine(
                applicationVariantId,
                mainQuantity,
                buildAddonProperties(
                  group,
                  panel,
                  customizationId,
                  bulkBuildId
                )
              );

            if (addonLine) {
              items.push(
                addonLine
              );
            }
          }

          continue;
        }

        if (
          addonMode ===
          'variant_from_option'
        ) {
          if (
            !panelsWithData.length
          ) {
            continue;
          }

          for (
            const panel of panelsWithData
          ) {
            if (
              !pricingSourceFieldKey
            ) {
              return {
                valid: false,
                message:
                  'A customization add-on is missing its pricing source field.',
                items: [],
                field: null,
                customizationId,
                bulkBuildId
              };
            }

            const optionVariantId =
              getSelectedOptionAddonVariantId(
                panel,
                pricingSourceFieldKey
              );

            if (
              !optionVariantId
            ) {
              const pricingSourceField =
                resolveField(
                  panel,
                  pricingSourceFieldKey
                );

              if (
                pricingSourceField
              ) {
                setFieldInvalid(
                  pricingSourceField,
                  'Please select an option.'
                );
              }

              return {
                valid: false,
                message:
                  'Please complete all required customization fields.',
                items: [],
                field:
                  pricingSourceField ||
                  null,
                customizationId,
                bulkBuildId
              };
            }

            const addonLine =
              buildAddonLine(
                optionVariantId,
                mainQuantity,
                buildAddonProperties(
                  group,
                  panel,
                  customizationId,
                  bulkBuildId
                )
              );

            if (addonLine) {
              items.push(
                addonLine
              );
            }
          }
                }
      }

      /*
       * Add option-triggered one-time products after
       * all standard customization add-ons.
       */
      if (includeOneTimeAddons) {
        const oneTimeAddons =
          getAllOneTimeAddons();

        oneTimeAddons.forEach(
          function (addon) {
            const oneTimeAddonLine =
              buildAddonLine(
                addon.variantId,
                1,
                buildOneTimeAddonProperties(
                  addon.group,
                  addon,
                  customizationId,
                  bulkBuildId
                )
              );

            if (oneTimeAddonLine) {
              items.push(
                oneTimeAddonLine
              );
            }
          }
        );
      }

      return {
        valid: true,
        message: '',
        items,
        field: null,
        customized: true,
        customizationId,
        bulkBuildId
      };
    }

    function buildProductCartItems() {
      return buildItemsForVariant({
        variantId:
          findMainVariantId(
            productForm
          ),

        quantity:
          findMainQuantity(
            productForm
          )
      });
    }

    function buildBulkCartItems(
  items,
  suppliedBulkBuildId
) {
  const normalizedItems =
    normalizeBulkSelection(
      items
    );

  if (
    !normalizedItems.length
  ) {
    return {
      valid: false,
      message:
        'Please enter at least one product quantity.',
      items: [],
      field: null,
      bulkBuildId: ''
    };
  }

  const bulkBuildId =
    suppliedBulkBuildId ||
    makeBulkBuildId();

  const cartItems = [];

  for (
    let index = 0;
    index <
    normalizedItems.length;
    index += 1
  ) {
    const item =
      normalizedItems[index];

    const result =
      buildItemsForVariant({
        variantId:
          item.id,

        quantity:
          item.quantity,

        bulkBuildId,

        includeOneTimeAddons:
          index === 0,

        customizationId:
          `${bulkBuildId}-variant-${item.id}-${Math.random()
            .toString(36)
            .slice(2, 7)}`
      });

    if (!result.valid) {
      return {
        valid: false,
        message:
          result.message,
        items: [],
        field:
          result.field ||
          null,
        customized:
          hasCustomizationData(),
        bulkBuildId
      };
    }

    cartItems.push(
      ...result.items
    );
  }

  /*
   * Temporary verification.
   * Remove after confirming the bulk payload.
   */
  console.log(
    'Bulk CPA cart items:',
    cartItems
  );

  console.table(
    cartItems.map(
      function (item) {
        return {
          variantId:
            item.id,

          quantity:
            item.quantity,

          lineType:
            item.properties &&
            item.properties[
              '_CPA Line Type'
            ],

          quantityMode:
            item.properties &&
            item.properties[
              '_Add-On Quantity Mode'
            ],

          multiplier:
            item.properties &&
            item.properties[
              '_Parent Quantity Multiplier'
            ],

          customizationId:
            item.properties &&
            item.properties[
              '_Customization ID'
            ],

          bulkBuildId:
            item.properties &&
            item.properties[
              '_Bulk Build ID'
            ],

          oneTime:
            item.properties &&
            item.properties[
              '_One-Time Add-On'
            ]
        };
      }
    )
  );

  return {
    valid: true,
    message: '',
    items:
      cartItems,
    field: null,
    customized:
      hasCustomizationData(),
    bulkBuildId
  };
}
    

    function setFormLoading(
      isLoading
    ) {
      if (!productForm) {
        return;
      }

      const buttons =
        productForm.querySelectorAll(
          '[type="submit"], button[name="add"]'
        );

      buttons.forEach(
        function (button) {
          button.disabled =
            isLoading;

          button.classList.toggle(
            'is-loading',
            isLoading
          );
        }
      );
    }

    async function submitCartPayload(
  items
) {
  const response =
    await fetch(
      '/cart/add.js',
      {
        method: 'POST',
        credentials:
          'same-origin',
        headers: {
          'Content-Type':
            'application/json',
          Accept:
            'application/json'
        },
        body: JSON.stringify({
          items
        })
      }
    );

  if (!response.ok) {
    const error =
      await response
        .json()
        .catch(
          function () {
            return null;
          }
        );

    throw new Error(
      (error &&
        (error.description ||
          error.message)) ||
        'Unable to add item to cart.'
    );
  }

  return response.json();
}

    async function handleProductFormSubmit(
      event
    ) {
      if (
        context !== 'product' ||
        !productForm ||
        !hasCustomizationData()
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (
        typeof event.stopImmediatePropagation ===
        'function'
      ) {
        event.stopImmediatePropagation();
      }

      updateAllConditions();
      updateSummary();
      updateOptionMetaActions();

      const payload =
        buildProductCartItems();

      if (!payload.valid) {
  setCpaAccordionOpen(
    true
  );

  if (payload.field) {
    const group =
      payload.field.closest(
        '[data-cpa-group]'
      );

          if (group) {
            activateGroup(
              group
            );
          }

          scrollToField(
            payload.field
          );

          focusField(
            payload.field
          );
        }

        alert(
          payload.message
        );

        return;
      }

      setFormLoading(true);

      try {
        await submitCartPayload(
          payload.items
        );

        window.location.href =
          '/cart';
      } catch (error) {
        alert(
          error.message ||
            'Unable to add item to cart.'
        );
      } finally {
        setFormLoading(false);
      }
    }

    if (
  accordionToggle &&
  accordionPanel
) {
  accordionToggle.addEventListener(
    'click',
    function () {
      const isOpen =
        accordionToggle.getAttribute(
          'aria-expanded'
        ) === 'true';

      setCpaAccordionOpen(
        !isOpen
      );
    }
  );
}

        root.addEventListener(
      'click',
      function (event) {
        const summaryRemoveApplication =
          event.target.closest(
            '[data-cpa-summary-remove-application]'
          );

        if (
          summaryRemoveApplication &&
          root.contains(
            summaryRemoveApplication
          )
        ) {
          removeApplicationSelection(
            summaryRemoveApplication
              .dataset.groupKey ||
              '',
            summaryRemoveApplication
              .dataset.applicationKey ||
              ''
          );

          return;
        }

        const groupToggle =
          event.target.closest(
            '[data-cpa-group-toggle]'
          );

        if (
          groupToggle &&
          root.contains(
            groupToggle
          )
        ) {
          const group =
            groupToggle.closest(
              '[data-cpa-group]'
            );

          if (group) {
            activateGroup(group);
          }

          return;
        }

        const applicationButton =
          event.target.closest(
            '[data-cpa-application-toggle]'
          );

        if (
          applicationButton &&
          root.contains(
            applicationButton
          )
        ) {
          handleApplicationToggle(
            applicationButton
          );

          return;
        }

        const choice =
          event.target.closest(
            '[data-cpa-choice]'
          );

        if (
          choice &&
          root.contains(choice)
        ) {
          handleChoiceClick(
            choice
          );

          return;
        }

        const infoTrigger =
          event.target.closest(
            '[data-cpa-option-info-trigger]'
          );

        if (
          infoTrigger &&
          root.contains(
            infoTrigger
          )
        ) {
          openInfoModal(
            infoTrigger.dataset
              .infoText,
            infoTrigger.dataset
              .infoLabel
          );

          return;
        }

        const placementGuideTrigger =
          event.target.closest(
            '[data-cpa-placement-guide-trigger]'
          );

        if (
          placementGuideTrigger &&
          root.contains(
            placementGuideTrigger
          )
        ) {
          openPreviewModal(
            placementGuideTrigger
              .dataset
              .placementGuideImage,
            placementGuideTrigger
              .dataset
              .placementGuideTitle
          );

          return;
        }

        const featuredExamplesToggle =
          event.target.closest(
            '[data-cpa-featured-examples-toggle]'
          );

        if (
          featuredExamplesToggle &&
          root.contains(
            featuredExamplesToggle
          )
        ) {
          const featuredExamples =
            featuredExamplesToggle.closest(
              '[data-cpa-featured-examples]'
            );

          const panel =
            featuredExamples
              ? featuredExamples.querySelector(
                  '[data-cpa-featured-examples-panel]'
                )
              : null;

          const label =
            featuredExamples
              ? featuredExamples.querySelector(
                  '[data-cpa-featured-examples-toggle-label]'
                )
              : null;

          const icon =
            featuredExamples
              ? featuredExamples.querySelector(
                  '.CPA__FeaturedExamplesToggleIcon'
                )
              : null;

          const isExpanded =
            featuredExamplesToggle.getAttribute(
              'aria-expanded'
            ) === 'true';

          featuredExamplesToggle.setAttribute(
            'aria-expanded',
            isExpanded
              ? 'false'
              : 'true'
          );

          if (panel) {
            if (isExpanded) {
              panel.classList.remove(
                'is-open'
              );

              panel.classList.add(
                'is-closing'
              );

              window.setTimeout(
                function () {
                  panel.hidden = true;

                  panel.classList.remove(
                    'is-closing'
                  );
                },
                220
              );
            } else {
              panel.hidden = false;

              window.requestAnimationFrame(
                function () {
                  panel.classList.remove(
                    'is-closing'
                  );

                  panel.classList.add(
                    'is-open'
                  );
                }
              );
            }
          }

          if (label) {
            label.textContent =
              isExpanded
                ? 'View Examples'
                : 'Hide Examples';
          }

          if (icon) {
            icon.textContent =
              isExpanded
                ? '+'
                : '×';
          }

          return;
        }

        const featuredExampleTrigger =
          event.target.closest(
            '[data-cpa-featured-example-trigger]'
          );

        if (
          featuredExampleTrigger &&
          root.contains(
            featuredExampleTrigger
          )
        ) {
          openPreviewModal(
            featuredExampleTrigger
              .dataset
              .featuredExampleImage,
            ''
          );

          return;
        }

        const previewTrigger =
          event.target.closest(
            '[data-cpa-option-preview-trigger]'
          );

        if (
          previewTrigger &&
          root.contains(
            previewTrigger
          )
        ) {
          openPreviewModal(
            previewTrigger.dataset
              .previewImage,
            previewTrigger.dataset
              .previewLabel
          );
        }
      }
    );

    document.addEventListener(
      'click',
      function (event) {
        const previewClose =
          event.target.closest(
            '[data-cpa-preview-close]'
          );

        const infoClose =
          event.target.closest(
            '[data-cpa-info-close]'
          );

        if (
          previewClose &&
          previewModal &&
          previewModal.contains(
            previewClose
          )
        ) {
          event.preventDefault();
          event.stopPropagation();

          closePreviewModal();

          return;
        }

        if (
          infoClose &&
          infoModal &&
          infoModal.contains(
            infoClose
          )
        ) {
          event.preventDefault();
          event.stopPropagation();

          closeInfoModal();
        }
      }
    );

    root.addEventListener(
      'input',
      function (event) {
        const field =
          event.target.closest(
            '[data-cpa-field]'
          );

        if (
          field &&
          root.contains(field)
        ) {
          clearFieldInvalidIfComplete(
            field
          );

          updateAllConditions();
          updateSummary();
          updateOptionMetaActions();

          return;
        }

        if (
          event.target.matches(
            '[data-cpa-group-notes-input]'
          ) &&
          root.contains(
            event.target
          )
        ) {
          updateSummary();
        }
      }
    );

    root.addEventListener(
      'change',
      function (event) {
        if (
          event.target.matches(
            '[data-cpa-input]'
          )
        ) {
          const field =
            event.target.closest(
              '[data-cpa-field]'
            );

          if (field) {
            clearFieldInvalidIfComplete(
              field
            );
          }

          updateAllConditions();
          updateSummary();
          updateOptionMetaActions();
        }
      }
    );

    document.addEventListener(
      'keydown',
      function (event) {
        if (
          event.key !== 'Escape'
        ) {
          return;
        }

        if (
          previewModal &&
          !previewModal.hidden
        ) {
          closePreviewModal();
        }

        if (
          infoModal &&
          !infoModal.hidden
        ) {
          closeInfoModal();
        }
      }
    );

    if (
      context === 'product' &&
      productForm
    ) {
      productForm.addEventListener(
        'submit',
        handleProductFormSubmit,
        true
      );

      const addButton =
        findAddButton(
          productForm
        );

      if (addButton) {
        addButton.addEventListener(
          'click',
          function (event) {
            if (
              !hasCustomizationData()
            ) {
              return;
            }

            event.preventDefault();
            event.stopPropagation();

            if (
              typeof event.stopImmediatePropagation ===
              'function'
            ) {
              event.stopImmediatePropagation();
            }

            handleProductFormSubmit(
              event
            );
          },
          true
        );
      }

      productForm.addEventListener(
        'change',
        function (event) {
          if (
            event.target.matches(
              '[name="id"], [name="quantity"], input.QuantitySelector__CurrentQuantity, input[data-quantity-input]'
            )
          ) {
            updateSummary();
          }
        }
      );

      productForm.addEventListener(
        'input',
        function (event) {
          if (
            event.target.matches(
              '[name="quantity"], input.QuantitySelector__CurrentQuantity, input[data-quantity-input]'
            )
          ) {
            updateSummary();
          }
        }
      );
    }

    const publicApi = {
      root,
      context,
      instanceId,
      registryKey,

      hasCustomizationData,

      updateConditions:
        updateAllConditions,

      updateSummary,

      updateOptionMetaActions,

      setBulkSelection,

      getBulkSelection:
        function () {
          return bulkSelectionItems.slice();
        },

      getBulkSelectionQuantity,

      getBulkSelectionProductTotal,

      buildItemsForVariant,

      buildBulkCartItems,

      buildProductCartItems,

      makeBulkBuildId,

      validate:
        validateCustomization,

      reset:
        function () {
          groups.forEach(
            function (group) {
              const setupPanel =
                getSetupPanel(
                  group
                );

              if (setupPanel) {
                clearSetupPanel(
                  setupPanel
                );
              }

              group
                .querySelectorAll(
                  '[data-cpa-application-panel]'
                )
                .forEach(
                  function (panel) {
                    clearApplicationPanel(
                      panel
                    );
                  }
                );

              const notesInput =
                group.querySelector(
                  '[data-cpa-group-notes-input]'
                );

              if (notesInput) {
                notesInput.value =
                  '';
              }

              updateApplicationButtons(
                group
              );

              updateGroupNotesVisibility(
                group
              );
            }
          );

          bulkSelectionItems = [];

          initializeAutoSelectedFields();

          updateAllConditions();
          updateSummary();
          updateOptionMetaActions();
        }
    };

    window.DACPA.instances.set(
      root.id,
      publicApi
    );

    window.DACPA.instances.set(
      registryKey,
      publicApi
    );

    initializeAutoSelectedFields();

    groups.forEach(
      function (group) {
        updateApplicationButtons(
          group
        );
      }
    );

    initGroupTabs();
    updateAllConditions();
    updateSummary();
    updateOptionMetaActions();

    root.dispatchEvent(
      new CustomEvent(
        'cpa:ready',
        {
          bubbles: true,
          detail: {
            context,
            instanceId,
            registryKey,
            api: publicApi
          }
        }
      )
    );
  }

  function initAllCustomProductAttributes() {
    document
      .querySelectorAll(
        '[data-cpa-root]'
      )
      .forEach(
        function (root) {
          initCustomProductAttributes(
            root
          );
        }
      );
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initAllCustomProductAttributes
    );
  } else {
    initAllCustomProductAttributes();
  }

  document.addEventListener(
    'shopify:section:load',
    initAllCustomProductAttributes
  );

document.addEventListener('change', function (event) {
  const input = event.target.closest('[data-cpa-file-input]');

  if (!input) {
    return;
  }

  const field = input.closest('[data-cpa-field]');

  if (!field) {
    return;
  }

  const uploadBox = field.querySelector('.CPA__FileUploadBox');
  const status = field.querySelector('[data-cpa-file-status]');
  const file = input.files && input.files[0];

  if (!uploadBox || !status) {
    return;
  }

  if (file) {
    status.textContent = file.name;
    uploadBox.classList.add('has-file');
  } else {
    status.textContent = 'No file selected';
    uploadBox.classList.remove('has-file');
  }
});
})();